import React from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { colors, spacing, radius, typography, shadows } from '../../containers/RecipeDesigner/styles/designTokens';

// Match the breakpoint where QNavBar overlays the page, including phone landscape.
export const useMobileNavigation = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
};

export function focusNavigationDestination() {
  document.getElementById('qflow-main-content')?.focus({ preventScroll: true });
}

export default function NavigationSearchDialog({ open, onClose, navigated = false, children }) {
  const mobile = useMobileNavigation();
  const titleId = React.useId();
  const [viewport, setViewport] = React.useState(null);

  React.useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!open || !mobile || !visualViewport) return undefined;

    // Mobile keyboards resize the visual viewport without always resizing 100vh.
    const update = () => setViewport({ height: visualViewport.height, top: visualViewport.offsetTop });
    update();
    visualViewport.addEventListener('resize', update);
    visualViewport.addEventListener('scroll', update);
    return () => {
      visualViewport.removeEventListener('resize', update);
      visualViewport.removeEventListener('scroll', update);
    };
  }, [mobile, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={mobile}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
      disableRestoreFocus={navigated}
      TransitionProps={{ onExited: () => { if (navigated) focusNavigationDestination(); } }}
      sx={mobile ? {
        top: viewport?.top || 0,
        height: viewport?.height || '100dvh',
        bottom: 'auto',
      } : undefined}
      PaperProps={{ sx: {
        alignSelf: 'flex-start',
        mt: mobile ? 0 : { xs: 4, sm: 8 },
        borderRadius: mobile ? 0 : radius.lg,
        overflow: 'hidden',
        boxShadow: shadows.dialog,
        ...(mobile && {
          height: '100%', maxHeight: '100%',
          pt: 'env(safe-area-inset-top)', pb: 'env(safe-area-inset-bottom)',
          pl: 'env(safe-area-inset-left)', pr: 'env(safe-area-inset-right)',
        }),
      } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: spacing.lg, py: spacing.xs, flexShrink: 0 }}>
        <Typography id={titleId} component="h2" sx={{ ...typography.stepLabel, color: colors.text.primary }}>
          Explore
        </Typography>
        <IconButton aria-label="Close search" onClick={onClose} sx={{ width: 44, height: 44 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...(mobile && { flex: 1 }), pb: spacing.sm }}>
        {children}
      </Box>
    </Dialog>
  );
}
